import type { Session, SessionPlace, SessionSpeaker } from './types'

export type FilterOption<T> = {
  id: T
  label: string
}

export type ScheduleFilterOptions = {
  places: FilterOption<string>[]
  speakers: FilterOption<number>[]
  kinds: FilterOption<string>[]
}

export type DeriveFilterOptionsConfig = {
  /**
   * Rótulo de exibição do tipo. Sem ele o filtro cai no slug — que é o enum
   * cru da api (`talk`, `check_in`) chegando ao participante.
   *
   * A resolução fica com o chamador porque combina duas fontes que não cabem
   * num módulo puro: a tradução local (`getProgrammingKind` + i18n) e o rótulo
   * que a api manda no catálogo, usado para o tipo que o front ainda não
   * conhece.
   */
  resolveKindLabel?: (slug: string) => string
}

/**
 * Deriva as opções de filtro avançado a partir das sessões recebidas.
 * Extrai valores únicos de place, speaker e kind.
 *
 * **Passe o conjunto completo do evento, não o do dia selecionado.** Derivar do
 * dia era o defeito reportado: local e palestrante que existiam na base sumiam
 * do filtro só porque não estavam naquele recorte, e o participante não
 * conseguia localizar a atividade.
 */
export const deriveFilterOptions = (
  sessions: Session[],
  { resolveKindLabel }: DeriveFilterOptionsConfig = {}
): ScheduleFilterOptions => {
  const placesMap = new Map<string, string>()
  const speakersMap = new Map<number, string>()
  const kindsSet = new Set<string>()

  for (const session of sessions) {
    if (session.place?.name) {
      placesMap.set(session.place.name, session.place.name)
    }

    if (session.speakers) {
      for (const speaker of session.speakers) {
        if (speaker?.id && speaker?.name) {
          speakersMap.set(speaker.id, speaker.name)
        }
      }
    }

    if (session.kind) {
      kindsSet.add(session.kind)
    }
  }

  const places: FilterOption<string>[] = Array.from(placesMap.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const speakers: FilterOption<number>[] = Array.from(speakersMap.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const kinds: FilterOption<string>[] = Array.from(kindsSet)
    .map(slug => ({ id: slug, label: resolveKindLabel?.(slug) ?? slug }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return { places, speakers, kinds }
}
